import {
  ShieldCheck,
  CheckCircle,
  DollarSign,
  Home,
  ThumbsUp,
  Ruler,
  CalendarClock,
  Hammer,
  Users,
  Wrench,
} from 'lucide-react';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import Split from '../../components/Split';
import CTASection from '../../components/CTASection';
import Benefits from '../../components/Benefits';
import ContentBlock from '../../components/ContentBlock';
import ProcessRoadmap from '../../components/ProcessRoadmap';
import FAQ from '../../components/FAQ';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import QuickQuoteForm from '../../components/QuickQuoteForm';
import { generateServiceSchema } from '../../utils/generateServiceSchema';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import { generateFAQSchema } from '../../utils/generateFAQSchema';
import FAQData from '../../data/faq/solar-panels.js';



export default function SolarPanelsPage() {
  const service = {
    title: 'Solar Panels',
    image: '/hero/best-solar-panel-services-in-los-angeles.webp',
    description:
      'Modern Build installs high-efficiency solar panels across Los Angeles, helping homeowners reduce energy bills and increase property value with clean, sustainable power.',
    link: '/services/solar-panels',
  };

  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}${service.link}`;

  const serviceSchema = generateServiceSchema(service, pageUrl);

  // Default location for static service page
  const defaultLocation = ' in Los Angeles, CA';

  // Replace <IN_CITY_ZIP> placeholders in the FAQ answers
  const mergedFaqs = FAQData.map(faq => ({
    ...faq,
    question: faq.question.replace(/<IN_CITY_ZIP>/g, defaultLocation),
    answer: faq.answer.replace(/<IN_CITY_ZIP>/g, defaultLocation),
  }));

      // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      { "@type": "ListItem", "position": 3, "name": "Solar Panels Services", "item": pageUrl }
    ]
  };

  return (
    <div className="bg-white">
    <>
      <Head>
        <title>Solar Panels Installation | Free Estimate | Modern Build</title>
        <meta name="title" content="Solar Panels Installation | Free Estimate | Modern Build" />
        <meta
          name="description"
          content="Solar panel installation by Modern Build. Lower energy costs, increase home value. Free estimate: (323) 300-4130"
        />
        <meta
          name="keywords"
          content="solar panels Los Angeles, solar installation, residential solar systems, clean energy, solar contractors LA, Modern Build"
        />
        <meta name="author" content="Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/services/solar-panels" />

        <meta property="og:title" content="Solar Panels Installation | Free Estimate | Modern Build" />
        <meta
          property="og:description"
          content="Solar panel installation by Modern Build. Lower energy costs, increase home value. Free estimate: (323) 300-4130"
        />
        <meta
          property="og:image"
          content="https://modernbuildremodel.com/hero/best-solar-panel-services-in-los-angeles.webp"
        />
        <meta
          property="og:url"
          content="https://modernbuildremodel.com/services/solar-panels"
        />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Solar Panels Installation | Free Estimate | Modern Build" />
        <meta
          name="twitter:description"
          content="Solar panel installation by Modern Build. Lower energy costs, increase home value. Free estimate: (323) 300-4130"
        />
        <meta
          name="twitter:image"
          content="https://modernbuildremodel.com/hero/best-solar-panel-services-in-los-angeles.webp"
        />
        <meta name="twitter:site" content="@modernbuildhome" />

        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Solar Panels Installation | Free Estimate | Modern Build',
                description:
                  'Reduce your utility bills and increase your home’s value with professional solar panel installation by Modern Build in Los Angeles.',
                slug: '/services/solar-panels',
              })
            ),
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />        

        {/* JSON-LD: FAQ */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema({
            service: service.title,
            city: 'Studio City',
            zip: '91604',
            faqs: mergedFaqs,
          }))
        }} />
      </Head>

      <Header />
      <Hero category="solarPanels" page="page" hideButton={true} hasContentH1={false}/>

      <QuickQuoteForm serviceTitle={service.title} />

      <Split
        subtitle="SOLAR ENERGY SERVICES"
        title="Power Your Home with Solar Panels"
        background="white"
        description="Modern Build provides top-tier solar panel installation services that help Los Angeles homeowners reduce energy bills, increase property value, and contribute to a more sustainable future. By harnessing clean solar energy, you can minimize your dependence on the grid and protect your home from rising utility costs."
        extraText="Our solar solutions are tailored specifically to your property’s roofline, sun exposure, energy consumption, and aesthetic preferences. We manage everything from system design and permitting to installation and final inspection, ensuring a hassle-free transition to renewable energy that performs reliably for decades."
        phone="(323) 300-4130"
        image1="/split/solar-installation-los-angeles-home.webp"
        image2="/split/residential-solar-panels-los-angeles.webp"
      />

<Benefits
  title="Why Choose Modern Build for Solar Panel Installation?"
  subtitle="Turn sunlight into savings with expert installation"
  items={[
    {
      icon: <ShieldCheck className="w-8 h-8 text-primary" />,
      title: "Certified Solar Experts",
    },
    {
      icon: <CheckCircle className="w-8 h-8 text-primary" />,
      title: "Custom System Design",
    },
    {
      icon: <Hammer className="w-8 h-8 text-primary" />,
      title: "Roof-Integrated Mounting",
    },
    {
      icon: <Ruler className="w-8 h-8 text-primary" />,
      title: "Optimized for Max Efficiency",
    },
    {
      icon: <Wrench className="w-8 h-8 text-primary" />,
      title: "Seamless Installation",
    },
    {
      icon: <DollarSign className="w-8 h-8 text-primary" />,
      title: "Lower Monthly Bills",
      href: "/services/energy-efficient-windows",
    },
    {
      icon: <ThumbsUp className="w-8 h-8 text-primary" />,
      title: "Federal & State Incentives",
    },
    {
      icon: <Home className="w-8 h-8 text-primary" />,
      title: "Boost Home Value",
    },
  ]}
/>


      <ContentBlock
        subtitle="LOS ANGELES SOLAR SPECIALISTS"
        title="Clean, Renewable Energy at Your Fingertips"
        description="Our solar systems are built to perform. We handle everything from permitting to final inspection so you can enjoy hassle-free energy savings."
        extraText="Modern Build ensures every installation is customized to your roof structure, energy needs, and long-term plans."
        buttonText="Request a Free Solar Consultation"
        background="white"
      />

      <ProcessRoadmap
        title="Our Solar Installation Process"
        subtitle="Transparent steps, real results"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "Energy Assessment",
            description: "We evaluate your energy usage, roof condition, and solar potential to recommend the ideal system.",
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Custom System Design",
            description: "Every home is unique. We engineer a solution to fit your needs, aesthetics, and long-term savings goals.",
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Professional Installation",
            description:
              "Our network of licensed installers mounts and connects your solar panels with minimal disruption to your property.",
          },
          {
            icon: <CheckCircle className="w-10 h-10 text-primary" />,
            title: "System Activation",
            description: "Once approved by your utility company, you’re ready to start generating clean power and saving money.",
          },
        ]}
      />

      <Split
        subtitle="LONG-TERM SAVINGS"
        title="Make the Switch to Solar Power"
        background="white"
        description="Solar energy isn’t just sustainable, it's financially smart. Reduce your utility bills, protect against rate hikes, and enjoy decades of reliable energy."
        extraText="Let Modern Build be your guide to a more energy-efficient and eco-friendly home."
        phone="(323) 300-4130"
        image1="/split/solar-panels-on-roof-los-angeles.webp"
        image2="/split/eco-friendly-home-with-solar-los-angeles.webp"
        reverse={true}
      />

      <CTASection
        title="Get a Free Estimate for Solar Panel Installation"
        subtitle="Take control of your energy future today"
        buttonText="Book Your Free Estimate"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />

      <FAQ category="solar-panels" />
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
    </div>
  );
}
